import { useEffect, useRef, useState } from "react";
import { Device, Call } from "@twilio/voice-sdk";
import { useLazyGetTelephonySettingsQuery } from "@/store/rest_api";
import { applyPreferredMicrophone } from "@/components/dialer/util/MicrophoneUtil";

export interface TwilioDeviceState {
  isReady: boolean;
  error: string | null;
  incomingCall: Call | null;
}

export function useTwilioDevice(
  onIncoming?: (call: Call) => void,
  onDeviceReady?: (device: Device) => void,
): TwilioDeviceState {
  const [getTelephonySettings] = useLazyGetTelephonySettingsQuery();
  const deviceRef = useRef<Device | null>(null);
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    const handler = () => {
      console.log("User interacted, initializing Twilio device");
      setUserInteracted(true);
    };
    window.addEventListener("click", handler, { once: true });
    window.addEventListener("keydown", handler, { once: true });
    return () => {
      window.removeEventListener("click", handler);
      window.removeEventListener("keydown", handler);
    };
  }, []);

  useEffect(() => {
    console.log("userInteracted effect fired", userInteracted);

    if (!userInteracted) {
      return;
    }

    let device: Device;
    let cancelled = false;

    const init = async () => {
      console.log("Initializing Twilio device...");

      try {
        const { data: settings } = await getTelephonySettings();
        if (!settings) {
          throw new Error("Failed to fetch telephony settings");
        }
        console.log("Got voice token, registering device...");
        if (cancelled) {
          return;
        }

        device = new Device(settings.voiceToken, {
          logLevel: "warn",
          allowIncomingWhileBusy: true,
        });

        device.on("registered", () => {
          console.log("Twilio device registered successfully");
          setIsReady(true);
          setError(null);
        });

        device.on("error", (err) => {
          setError(err.message);
        });

        device.on("incoming", (call: Call) => {
          console.log("Twilio incoming call", call);
          setIncomingCall(call);
          onIncoming?.(call);
          call.on("disconnect", () => setIncomingCall(null));
          call.on("cancel", () => setIncomingCall(null));
          call.on("reject", () => setIncomingCall(null));
          call.on("accept", () => {
            void applyPreferredMicrophone(device);
          });
        });

        device.on("tokenWillExpire", async () => {
          try {
            const { data: refreshed } = await getTelephonySettings();
            if (refreshed) {
              device.updateToken(refreshed.voiceToken);
            }
          } catch {
            setError("Failed to refresh voice token");
          }
        });

        await device.register();
        deviceRef.current = device;
        onDeviceReady?.(device);
      } catch (e: any) {
        console.error("Failed to initialize Twilio device", e);
        setError(e?.data?.detail || "Failed to initialize voice device");
      }
    };

    init();

    return () => {
      cancelled = true;
      deviceRef.current?.destroy();
      deviceRef.current = null;
      setIsReady(false);
      setIncomingCall(null);
    };
  }, [userInteracted]);

  return { isReady, error, incomingCall };
}
